import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { matchesPhoneSearch } from '../../utils/phoneUtils';

const SearchableSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  className = "",
  error = "",
  required = false,
  disabled = false,
  size = "md",
  name = "",
  onScrollToEnd,
  isLoadingMore = false,
  hasMore = false,
  icon: Icon = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const portalRef = useRef(null);
  const dropdownUidRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const sizeClasses = {
    sm: "text-xs font-medium",
    md: "text-sm font-medium",
    lg: "text-base font-medium"
  };

  const inputPadding = {
    sm: "px-2 py-1.5",
    md: "px-3 py-2",
    lg: "px-4 py-2.5"
  };

  const computeDropdownStyle = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const showAbove = spaceBelow < 300 && spaceAbove > spaceBelow;

    setDropdownStyle(
      showAbove
        ? { position: 'fixed', bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width, zIndex: 99999 }
        : { position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, zIndex: 99999 }
    );
  }, []);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      if (typeof opt === 'string') {
        return opt.toLowerCase().includes(q) || matchesPhoneSearch(opt, search);
      }
      const optLabel = opt.label || '';
      const searchText = opt.searchText || '';
      const subText = opt.subText || '';
      const textMatches =
        optLabel.toLowerCase().includes(q) ||
        searchText.toLowerCase().includes(q) ||
        subText.toLowerCase().includes(q);
      if (textMatches) return true;

      const phoneCandidates = [opt.phone, opt.mobile, opt.telephone, optLabel, searchText].filter(Boolean);
      return phoneCandidates.some((p) => matchesPhoneSearch(p, search));
    });
  }, [options, search]);

  const selectedOption = useMemo(() => {
    return options.find((opt) => {
      const v = typeof opt === 'string' ? opt : opt.value;
      return v === value;
    });
  }, [options, value]);

  const selectedLabel = useMemo(() => {
    if (!selectedOption) return '';
    return typeof selectedOption === 'string' ? selectedOption : selectedOption.label;
  }, [selectedOption]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        portalRef.current && !portalRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setSearch('');
        setActiveIndex(-1);
      }
    };
    const handleOtherOpen = (e) => { if (e.detail !== dropdownUidRef) { setIsOpen(false); setSearch(''); setActiveIndex(-1); } };
    document.addEventListener('mousedown', handler);
    document.addEventListener('slim:dropdownOpened', handleOtherOpen);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('slim:dropdownOpened', handleOtherOpen);
    };
  }, []);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    const update = () => computeDropdownStyle();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [computeDropdownStyle, isOpen]);

  // Scroll active option into view
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const el = listRef.current?.querySelector(`[data-option-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const openDropdown = useCallback(() => {
    if (disabled) return;
    computeDropdownStyle();
    setIsOpen(true);
    document.dispatchEvent(new CustomEvent('slim:dropdownOpened', { detail: dropdownUidRef }));
    setActiveIndex(() => {
      if (filteredOptions.length === 0) return -1;
      const idx = filteredOptions.findIndex((opt) => (typeof opt === 'string' ? opt : opt.value) === value);
      return idx >= 0 ? idx : 0;
    });
  }, [computeDropdownStyle, disabled, filteredOptions, value]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    setActiveIndex(-1);
  }, []);

  const handleSelect = (optionValue) => {
    onChange({ target: { name, value: optionValue } });
    closeDropdown();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name, value: '' } });
    setSearch('');
    inputRef.current?.focus();
    openDropdown();
  };

  const handleInputFocus = (e) => {
    openDropdown();
    e.target.select();
  };

  const handleChevronMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (isOpen) { closeDropdown(); inputRef.current?.blur(); return; }
    openDropdown();
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    if (!isOpen) openDropdown();
    setSearch(e.target.value);
    setActiveIndex(-1);
  };

  const handleListScroll = (e) => {
    if (!onScrollToEnd || isLoadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) onScrollToEnd();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) { openDropdown(); return; }
      setActiveIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) { openDropdown(); return; }
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && isOpen) {
      e.preventDefault();
      if (activeIndex >= 0 && filteredOptions[activeIndex]) {
        const opt = filteredOptions[activeIndex];
        handleSelect(typeof opt === 'string' ? opt : opt.value);
      } else if (filteredOptions.length === 1) {
        const opt = filteredOptions[0];
        handleSelect(typeof opt === 'string' ? opt : opt.value);
      }
    } else if (e.key === 'Tab') {
      closeDropdown();
    } else if (e.key === 'Escape') {
      closeDropdown();
      inputRef.current?.blur();
    }
  };

  const inputValue = isOpen ? search : selectedLabel;
  const inputPlaceholder = isOpen ? 'Search...' : placeholder;

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {label && (
        <label className="block text-sm font-semibold text-gray-800 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div
        className={`w-full rounded border flex items-center justify-between transition-all overflow-hidden ${sizeClasses[size]} ${
          error
            ? 'border-red-300 bg-red-50 ring-1 ring-red-400'
            : 'border-gray-300 bg-white focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {Icon && (
          <div className="pl-3 flex items-center pointer-events-none">
            <Icon className={`text-gray-400 ${size === 'sm' ? 'h-3.5 w-3.5' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'}`} />
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={inputPlaceholder}
          disabled={disabled}
          className={`w-full bg-transparent border-0 ${inputPadding[size]} focus:outline-none focus:ring-0 ${sizeClasses[size]} ${
            inputValue ? 'text-gray-900' : 'text-gray-400'
          } ${disabled ? 'cursor-not-allowed' : 'cursor-text'} ${Icon ? 'pl-2' : ''} ${className.includes('font-mono') ? 'font-mono' : ''}`}
        />

        <div className="flex items-center gap-1 flex-shrink-0 pr-2">
          {value && !disabled && (
            <button type="button" onClick={handleClear} className="text-gray-300 hover:text-gray-500 transition-colors" title="Clear">
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onMouseDown={handleChevronMouseDown}
            disabled={disabled}
            className="rounded p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:cursor-not-allowed"
          >
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {isOpen && !disabled && createPortal(
        <div ref={portalRef} style={dropdownStyle} className="overflow-hidden rounded-lg border border-gray-100 bg-white shadow-xl shadow-gray-200/70 ring-1 ring-black/5">
          <div ref={listRef} className="max-h-60 overflow-y-auto" onScroll={handleListScroll}>
            {filteredOptions.length > 0 && (
              <div className="sticky top-0 bg-gray-50 border-b border-gray-100 px-3 py-1.5 text-xs text-gray-500 font-medium">
                {filteredOptions.length} option{filteredOptions.length !== 1 ? 's' : ''}{search ? ' found' : ''}
              </div>
            )}

            {filteredOptions.length === 0 && !isLoadingMore ? (
              <div className="px-3 py-4 text-center text-xs text-gray-500">No results found</div>
            ) : (
              <>
                {filteredOptions.map((option, index) => {
                  const optionValue = typeof option === 'string' ? option : option.value;
                  const optionLabel = typeof option === 'string' ? option : option.label;
                  const isSelected = optionValue === value;

                  return (
                    <button
                      data-option-index={index}
                      key={optionValue}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); handleSelect(optionValue); }}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`w-full text-left px-3 ${className.includes('font-mono') ? 'py-0.4' : 'py-2'} transition-colors border-l-2 ${sizeClasses[size]} ${
                        activeIndex === index
                          ? 'border-indigo-500 bg-indigo-50/80 text-indigo-700'
                          : isSelected
                          ? 'border-indigo-500 bg-indigo-50/80 text-indigo-700'
                          : 'border-transparent text-gray-900 hover:bg-gray-50'
                      } ${className.includes('font-mono') ? 'font-mono' : ''}`}
                    >
                      {optionLabel}
                    </button>
                  );
                })}

                {(isLoadingMore || hasMore) && (
                  <div className="flex items-center justify-center py-2 border-t border-gray-100">
                    {isLoadingMore ? (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <svg className="animate-spin h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Loading more...
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">Scroll for more</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        , document.body
      )}

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default SearchableSelect;
